/**
 * @file Showcase 表单进阶预览区块
 * @description Combobox / DatePicker / MultiSelect / Slider / Upload 等预览。
 *
 * @whenToUse 仅由 `src/pages/showcase-page.tsx` 组合引用。
 * @whenNotToUse 业务页面或通用 ui 控件实现。
 */

import { useState } from 'react'
import {
  CalendarDays,
  CalendarRange,
  ChevronsUpDown,
  ListFilter,
  SlidersHorizontal,
  Upload as UploadIcon,
} from 'lucide-react'
import comboboxPrompt from '@/docs/components/combobox.md?raw'
import datePickerPrompt from '@/docs/components/date-picker.md?raw'
import dateRangePickerPrompt from '@/docs/components/date-range-picker.md?raw'
import multiSelectPrompt from '@/docs/components/multi-select.md?raw'
import sliderPrompt from '@/docs/components/slider.md?raw'
import uploadPrompt from '@/docs/components/upload.md?raw'
import { Combobox } from '@/components/ui/combobox.tsx'
import { DatePicker } from '@/components/ui/date-picker.tsx'
import { DateRangePicker } from '@/components/ui/date-range-picker.tsx'
import { FormField } from '@/components/ui/form-field.tsx'
import { MultiSelect } from '@/components/ui/multi-select.tsx'
import { ShowcaseCard } from '@/components/ui/showcase-card.tsx'
import { Slider } from '@/components/ui/slider.tsx'
import { Upload } from '@/components/ui/upload.tsx'

const cityOptions = [
  { value: 'bj', label: '北京' },
  { value: 'sh', label: '上海' },
  { value: 'gz', label: '广州' },
  { value: 'sz', label: '深圳' },
  { value: 'hz', label: '杭州' },
  { value: 'cd', label: '成都' },
  { value: 'wh', label: '武汉' },
  { value: 'xa', label: '西安' },
]

const tagOptions = [
  { value: 'react', label: 'React' },
  { value: 'vue', label: 'Vue' },
  { value: 'svelte', label: 'Svelte' },
  { value: 'angular', label: 'Angular' },
  { value: 'solid', label: 'Solid' },
]

export function ShowcaseFormAdvancedSections() {
  return (
    <>
      <ShowcaseCard
        id="date-picker"
        title="DatePicker"
        description="单日选择；值格式 YYYY-MM-DD；月历浮层。"
        icon={<CalendarDays className="size-4" aria-hidden />}
        prompt={datePickerPrompt}
      >
        <DatePickerDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="date-range-picker"
        title="DateRangePicker"
        description="日期区间；悬停预览范围。"
        icon={<CalendarRange className="size-4" aria-hidden />}
        prompt={dateRangePickerPrompt}
      >
        <DateRangeDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="combobox"
        title="Combobox"
        description="可搜索单选下拉。"
        icon={<ChevronsUpDown className="size-4" aria-hidden />}
        prompt={comboboxPrompt}
      >
        <ComboboxDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="multi-select"
        title="MultiSelect"
        description="多选下拉；默认无搜索，可开 showSearch；Tag +N。"
        icon={<ListFilter className="size-4" aria-hidden />}
        prompt={multiSelectPrompt}
      >
        <MultiSelectDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="upload"
        title="Upload"
        description="点击 / 拖拽选文件；本地 File 列表。"
        icon={<UploadIcon className="size-4" aria-hidden />}
        prompt={uploadPrompt}
      >
        <UploadDemo />
      </ShowcaseCard>

      <ShowcaseCard
        id="slider"
        title="Slider"
        description="自定义滑杆；键盘方向键可调。"
        icon={<SlidersHorizontal className="size-4" aria-hidden />}
        prompt={sliderPrompt}
      >
        <SliderDemo />
      </ShowcaseCard>
    </>
  )
}

function DatePickerDemo() {
  const [date, setDate] = useState('2026-08-05')
  return (
    <div className="max-w-md">
      <FormField label="生效日" htmlFor="demo-date">
        <DatePicker id="demo-date" value={date} onChange={setDate} />
      </FormField>
      <p className="mt-2 text-xs text-muted">当前：{date || '未选'}</p>
    </div>
  )
}

function DateRangeDemo() {
  const [range, setRange] = useState<{ from?: string; to?: string }>({
    from: '2026-08-01',
    to: '2026-08-12',
  })
  return (
    <div className="max-w-md">
      <FormField label="统计区间">
        <DateRangePicker value={range} onChange={setRange} />
      </FormField>
      <p className="mt-2 text-xs text-muted">
        {range.from && range.to
          ? `${range.from} ~ ${range.to}`
          : range.from
            ? `${range.from} ~ …`
            : '未选'}
      </p>
    </div>
  )
}

function ComboboxDemo() {
  const [city, setCity] = useState('sh')
  return (
    <div className="max-w-md">
      <Combobox
        value={city}
        onChange={setCity}
        options={cityOptions}
        placeholder="选择城市"
      />
    </div>
  )
}

function MultiSelectDemo() {
  const [tags, setTags] = useState(['react', 'vue'])
  return (
    <div className="max-w-md">
      <MultiSelect
        value={tags}
        onChange={setTags}
        options={tagOptions}
        placeholder="选择技术栈"
      />
    </div>
  )
}

function UploadDemo() {
  const [files, setFiles] = useState<File[]>([])
  return (
    <div className="max-w-md">
      <Upload value={files} onChange={setFiles} accept=".pdf,image/*" />
    </div>
  )
}

function SliderDemo() {
  const [volume, setVolume] = useState(42)
  return (
    <div className="max-w-md space-y-2">
      <div className="flex items-center justify-between text-sm">
        <span className="text-muted">音量</span>
        <span className="tabular-nums text-foreground">{volume}</span>
      </div>
      <Slider value={volume} onChange={setVolume} aria-label="音量" />
    </div>
  )
}
